import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { TIER_FEATURES } from '@/services/stripe'
import type { SubscriptionTier } from '@/types'

/**
 * Returns the display name of the next tier up, or null if already on the highest tier.
 * Handles both old (personal/commercial) and new (hobby/maker) DB values.
 */
export function getNextTierName(currentTier: SubscriptionTier): string | null {
  switch (currentTier) {
    case 'free': return 'Hobby'
    case 'personal':
    case 'hobby': return 'Maker'
    case 'commercial':
    case 'maker': return 'Farm'
    case 'farm': return null
    default: return 'Hobby'
  }
}

export function useTierLimits() {
  const authStore = useAuthStore()

  const tier = computed(() => authStore.subscriptionTier)
  const tierConfig = computed(() => TIER_FEATURES[tier.value])

  // Limit values (-1 = unlimited)
  const printerLimit = computed(() => tierConfig.value.printers)
  const filamentLimit = computed(() => tierConfig.value.filaments)
  const projectLimit = computed(() => tierConfig.value.projects)
  const storeLocationLimit = computed(() => tierConfig.value.storeLocations)
  const maxFileSizeMb = computed(() => tierConfig.value.maxFileSizeMb)
  const messagesPerDay = computed(() => tierConfig.value.messagesPerDay)
  const hasLabels = computed(() => tierConfig.value.labels)

  /**
   * Check if the user can create another item of the given type.
   * Returns { allowed: true } or { allowed: false, message: string }
   */
  function canCreate(type: 'printer' | 'filament' | 'project' | 'storeLocation', currentCount: number): { allowed: boolean; message?: string } {
    const limits: Record<string, number> = {
      printer: printerLimit.value,
      filament: filamentLimit.value,
      project: projectLimit.value,
      storeLocation: storeLocationLimit.value,
    }

    const limit = limits[type]
    if (limit === -1) return { allowed: true } // Unlimited

    if (currentCount >= limit) {
      const tierName = tierConfig.value.name
      const nextTier = getNextTierName(tier.value)
      const upgradeHint = nextTier ? ` Upgrade to ${nextTier} for more.` : ''
      return {
        allowed: false,
        message: `You've reached the ${tierName} plan limit of ${limit} ${type === 'storeLocation' ? 'storage locations' : type + 's'}.${upgradeHint}`
      }
    }

    return { allowed: true }
  }

  /**
   * Get remaining count for a resource type
   */
  function remaining(type: 'printer' | 'filament' | 'project' | 'storeLocation', currentCount: number): number | null {
    const limits: Record<string, number> = {
      printer: printerLimit.value,
      filament: filamentLimit.value,
      project: projectLimit.value,
      storeLocation: storeLocationLimit.value,
    }
    const limit = limits[type]
    if (limit === -1) return null // Unlimited
    return Math.max(0, limit - currentCount)
  }

  return {
    tier,
    tierConfig,
    printerLimit,
    filamentLimit,
    projectLimit,
    storeLocationLimit,
    maxFileSizeMb,
    messagesPerDay,
    hasLabels,
    canCreate,
    remaining,
  }
}
